import React from "react";
import {
  Typography,
  TextField,
  Button,
  Stack,
  // Paper,
  Box,
  MenuItem,
  Select,
  FormControl,

  FormHelperText,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
// import ShieldIcon from "@mui/icons-material/Shield";
import AuthCard from "../../components/layout/AuthCard";
import { useLocation } from "react-router-dom";
import ReqMark from "./ReqMark";

export default function Step3Verification({
  nextStep,
  prevStep,
  defaultValues = {},
}) {
  const [nationality, setNationality] = React.useState(
    defaultValues.nationality || ""
  );
  const [dateOfBirth, setDateOfBirth] = React.useState(
    defaultValues.dateOfBirth || ""
  );
  const [idType, setIdType] = React.useState(defaultValues.idType || "");
  const [address, setAddress] = React.useState(defaultValues.address || "");
  const [city, setCity] = React.useState(defaultValues.city || "");
  const [state, setState] = React.useState(defaultValues.state || "");
  const [zipCode, setZipCode] = React.useState(defaultValues.zipCode || "");
  const [idNumber, setIdNumber] = React.useState(defaultValues.idNumber || "");
  const [errors, setErrors] = React.useState({});
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const role = queryParams.get("role");

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "rgba(255,255,255,0.05)",
      borderRadius: "10px",
    },
  };

  const handleNextClick = () => {
    const newErrors = {};
    if (!dateOfBirth) newErrors.dateOfBirth = "Date of birth is required.";
    if (!nationality) newErrors.nationality = "Please select a nationality.";
    if (!address.trim()) newErrors.address = "Address is required.";
    if (!city.trim()) newErrors.city = "City is required.";
    if (!state.trim()) newErrors.state = "State/Province is required.";
    if (!zipCode.trim()) newErrors.zipCode = "Zip code is required.";
    if (!idType) newErrors.idType = "Please select an ID type.";
    if (!idNumber.trim()) newErrors.idNumber = "ID number is required.";
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    if (typeof nextStep === "function") {
      nextStep({ nationality, dateOfBirth, idType, address, city, state, zipCode, idNumber });
    }
  };

  return (
    <AuthCard>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={1}
      >
        <Typography variant="h6" fontWeight={700}>
          Create Account
        </Typography>
        <Button
          variant="outlined"
          size="small"
          sx={{
            color: "#38bdf8",
            borderColor: "rgba(56,189,248,0.4)",
            borderRadius: "999px",
            textTransform: "none",
            px: 2,
            height: 28,
            fontSize: 12,
            "&:hover": { background: "rgba(56,189,248,0.1)" },
          }}
        >
          {role === "investor"
            ? "Investor"
            : role === "assetOwner"
            ? "Asset Owner"
            : "Admin"}
        </Button>
      </Stack>
      <Typography color="text.secondary" sx={{ mb: 0.5 }}>
        Step 3 of 5 - Identity & Address
      </Typography>
      <Typography variant="caption" sx={{ display: "block", color: "#f87171", mb: 2 }}>
        * All fields in this step are required.
      </Typography>

      {/* Segmented Progress Bar */}
      <Stack direction="row" spacing={1} sx={{ mb: 4 }}>
        {[1, 2, 3, 4, 5].map((bar, index) => (
          <Box
            key={bar}
            sx={{
              flex: 1,
              height: 3,
              borderRadius: 1,
              backgroundColor: index < 3 ? "#0ea5e9" : "rgba(255,255,255,0.1)",
            }}
          />
        ))}
      </Stack>

      <Stack spacing={2}>
        <Box>
          <Typography
            variant="body2"
            sx={{ mb: 0.6, fontWeight: 700, color: "#cbd5e1" }}
          >
            Date of Birth
            <ReqMark />
          </Typography>
          <TextField
            type="date"
            fullWidth
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: new Date().toISOString().split("T")[0] }}
            value={dateOfBirth}
            onChange={(e) => { setDateOfBirth(e.target.value); setErrors((p) => ({ ...p, dateOfBirth: "" })); }}
            error={!!errors.dateOfBirth}
            helperText={errors.dateOfBirth}
            sx={fieldSx}
          />
        </Box>

        <Box>
          <Typography
            variant="body2"
            sx={{ mb: 0.6, fontWeight: 700, color: "#cbd5e1" }}
          >
            Nationality
            <ReqMark />
          </Typography>
          <TextField
            label="Select nationality"
            select
            fullWidth
            value={nationality}
            onChange={(e) => { setNationality(e.target.value); setErrors((p) => ({ ...p, nationality: "" })); }}
            error={!!errors.nationality}
            helperText={errors.nationality}
            sx={fieldSx}
          >
            <MenuItem value="saudiArabia">Saudi Arabia</MenuItem>
            <MenuItem value="unitedStates">United Arab Emirates</MenuItem>
          </TextField>
        </Box>

        <Box>
          <Typography
            variant="body2"
            sx={{ mb: 0.6, fontWeight: 700, color: "#cbd5e1" }}
          >
            Residential Address
            <ReqMark />
          </Typography>
          <TextField
            label="Street address"
            fullWidth
            value={address}
            onChange={(e) => { setAddress(e.target.value); setErrors((p) => ({ ...p, address: "" })); }}
            error={!!errors.address}
            helperText={errors.address}
            sx={fieldSx}
          />
        </Box>

        <Stack direction="row" spacing={2}>
          <Box>
            <Typography
              variant="body2"
              sx={{ mb: 0.6, fontWeight: 700, color: "#cbd5e1" }}
            >
              City
              <ReqMark />
            </Typography>
            <TextField
              label="City"
              fullWidth
              value={city}
              onChange={(e) => { setCity(e.target.value); setErrors((p) => ({ ...p, city: "" })); }}
              error={!!errors.city}
              helperText={errors.city}
              sx={fieldSx}
            />
          </Box>

          <Box>
            <Typography
              variant="body2"
              sx={{ mb: 0.6, fontWeight: 700, color: "#cbd5e1" }}
            >
              State/Province
              <ReqMark />
            </Typography>
            <TextField
              label="State/Province"
              fullWidth
              value={state}
              onChange={(e) => { setState(e.target.value); setErrors((p) => ({ ...p, state: "" })); }}
              error={!!errors.state}
              helperText={errors.state}
              sx={fieldSx}
            />
          </Box>
        </Stack>

        <Box>
          <Typography
            variant="body2"
            sx={{ mb: 0.6, fontWeight: 700, color: "#cbd5e1" }}
          >
            Zip Code
            <ReqMark />
          </Typography>
          <TextField
            label="Zip/Postal code"
            fullWidth
            value={zipCode}
            onChange={(e) => { setZipCode(e.target.value); setErrors((p) => ({ ...p, zipCode: "" })); }}
            error={!!errors.zipCode}
            helperText={errors.zipCode}
            sx={fieldSx}
          />
        </Box>

        <Box>
          <Typography
            variant="body2"
            sx={{ mb: 0.6, fontWeight: 700, color: "#cbd5e1" }}
          >
            ID Type
            <ReqMark />
          </Typography>
          <FormControl fullWidth error={!!errors.idType}>
            <Select
              displayEmpty
              value={idType}
              onChange={(e) => { setIdType(e.target.value); setErrors((p) => ({ ...p, idType: "" })); }}
              sx={fieldSx}
            >
              <MenuItem value="" disabled><em>Select ID type</em></MenuItem>
              <MenuItem value="passport">Passport</MenuItem>
              <MenuItem value="national_id">National ID</MenuItem>
              <MenuItem value="driversLicense">Driver's License</MenuItem>
            </Select>
            {errors.idType && <FormHelperText>{errors.idType}</FormHelperText>}
          </FormControl>
        </Box>

        <Box>
          <Typography
            variant="body2"
            sx={{ mb: 0.6, fontWeight: 700, color: "#cbd5e1" }}
          >
            ID Number
            <ReqMark />
          </Typography>
          <TextField
            label="ID number"
            fullWidth
            value={idNumber}
            onChange={(e) => { setIdNumber(e.target.value); setErrors((p) => ({ ...p, idNumber: "" })); }}
            error={!!errors.idNumber}
            helperText={errors.idNumber}
            sx={fieldSx}
          />
        </Box>
      </Stack>

      {/* <Paper
        sx={{
          mt: 3,
          p: 2,
          background: "rgba(255,255,255,0.03)",
          borderRadius: 2,
          border: "1px solid rgba(255,255,255,0.1)",
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <ShieldIcon color="primary" />
          <Typography variant="body2">
            KYC Verification Required — Your information is securely stored.
          </Typography>
        </Stack>
      </Paper> */}

      <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
        <Button
          variant="contained"
          startIcon={<ArrowBackIcon />}
          fullWidth
          sx={{
            background: "linear-gradient(90deg,#0284c7,#0ea5e9)",
            borderRadius: "12px",
            textTransform: "none",
          }}
          onClick={prevStep}
        >
          Back
        </Button>
        <Button
          variant="contained"
          endIcon={<ArrowForwardIcon />}
          fullWidth
          sx={{
            background: "linear-gradient(90deg,#0284c7,#0ea5e9)",
            borderRadius: "12px",
            textTransform: "none",
          }}
          onClick={handleNextClick}
        >
          Continue
        </Button>
      </Stack>
    </AuthCard>
  );
}
